/* ============================================================================
   Estado de la última ingesta. Vive en una hoja, accesible desde el chip del
   encabezado y desde el pie: quien opera la herramienta necesita saber en
   segundos si la corrida de este mes salió bien.
   ========================================================================== */

import { dataset, sourceName } from '../data/dataset'
import { formatPeriodTitle, integer, pluralize } from '../lib/format'

const STATUS_LABEL: Record<string, string> = {
  ok: 'Sin incidencias',
  'sin-cambios': 'Sin cambios',
  error: 'Con incidencias',
  omitido: 'Omitida',
}

export function EstadoDeDatos() {
  const { ingestion, coverage, latestPeriod } = dataset
  const problems = ingestion.results.filter((r) => r.status === 'error')

  return (
    <>
      <div className="stack stack--tight">
        <p className="t-micro ink-3">Resumen</p>
        <p className="t-body">
          Último periodo publicado: <strong>{formatPeriodTitle(latestPeriod)}</strong>. Cobertura de{' '}
          {formatPeriodTitle(coverage.firstPeriod).toLowerCase()} a{' '}
          {formatPeriodTitle(coverage.lastPeriod).toLowerCase()},{' '}
          {coverage.gaps.length === 0
            ? 'sin huecos'
            : `con ${coverage.gaps.length} ${pluralize(coverage.gaps.length, 'mes', 'meses')} sin dato`}
          .
        </p>
        {problems.length > 0 ? (
          <p className="t-footnote status-problem">
            {problems.length} {pluralize(problems.length, 'fuente', 'fuentes')} con{' '}
            {pluralize(problems.length, 'incidencia', 'incidencias')} en la última corrida.
          </p>
        ) : (
          <p className="t-footnote status-ok">Todas las fuentes respondieron correctamente.</p>
        )}
      </div>

      <div className="stack stack--tight">
        <p className="t-micro ink-3">Fuentes</p>
        <ul className="status-list">
          {ingestion.results.map((result) => (
            <li key={result.sourceId} className="status-list__item">
              <span
                className={`status-dot status-dot--${result.status === 'ok' ? 'ok' : result.status === 'error' ? 'error' : 'idle'}`}
                aria-hidden="true"
              />
              <div className="status-list__body">
                <p className="t-footnote status-list__name">{sourceName(result.sourceId)}</p>
                <p className="t-caption ink-2">{result.message}</p>
                <p className="t-caption ink-3">
                  {STATUS_LABEL[result.status] ?? result.status}
                  {result.pointsWritten > 0
                    ? ` · ${integer(result.pointsWritten)} puntos escritos`
                    : ''}
                  {result.latestPeriod ? ` · hasta ${formatPeriodTitle(result.latestPeriod).toLowerCase()}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="stack stack--tight">
        <p className="t-micro ink-3">Cómo se actualiza</p>
        <p className="t-footnote ink-2">
          El INEGI publica el boletín de Líneas de Pobreza entre el día 11 y el 13 de cada mes, con la
          cifra del mes anterior. Correr <code>npm run ingest</code> descarga las fuentes, valida el
          resultado y reescribe el dataset. Si la validación encuentra un error, el archivo no se
          reescribe: es preferible mostrar el mes pasado que un dato mal etiquetado.
        </p>
      </div>
    </>
  )
}
